import React from 'react';
import { Box, Typography, Paper } from '@material-ui/core';
import { ReportCardComponentProps } from '../schema-types';
import { processTextContent } from './text-utils';
import { filterDOMProps } from '../utils/prop-normalizer';

interface CalloutBoxItem {
  label: string;
  value: string;
}

interface CalloutBoxData {
  title?: string;
  content?: string;
  items?: CalloutBoxItem[];
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  borderRadius?: number;
  padding?: number;
  icon?: string;
  iconColor?: string;
  variant?: 'info' | 'warning' | 'success' | 'error' | 'custom';
  elevation?: number;
  centerContent?: boolean;
  titleStyle?: { [key: string]: any };
  contentStyle?: { [key: string]: any };
}

export const CalloutBoxComponent: React.FC<ReportCardComponentProps> = ({
  data,
  baseData,
  player,
  playerAssessment,
  reportCardLevel: _reportCardLevel,
  context,
  ...props
}) => {
  const {
    title,
    content,
    items,
    backgroundColor,
    borderColor,
    textColor,
    borderRadius = 8,
    padding = 16,
    icon,
    iconColor,
    variant = 'info',
    elevation = 2,
    centerContent = false,
    titleStyle = {},
    contentStyle = {}
  }: CalloutBoxData = data || {};

  // Get responsive context
  const isMobile = context?.responsive?.isMobile || false;

  // Parse content into items if items array is not provided
  const parseContentIntoItems = (content: string): CalloutBoxItem[] => {
    if (!content) return [];

    const lines = content.split('\\n').filter(line => line.trim());
    return lines.map(line => {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        return {
          label: line.substring(0, colonIndex).trim(),
          value: line.substring(colonIndex + 1).trim()
        };
      }
      return {
        label: '',
        value: line.trim()
      };
    });
  };

  // Use items if provided, otherwise parse content
  const finalItems = items || (content ? parseContentIntoItems(content) : []);

  // Default styles based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'info':
        return {
          backgroundColor: backgroundColor || '#e3f2fd',
          borderColor: borderColor || '#2196f3',
          textColor: textColor || '#1976d2',
          defaultIcon: 'ℹ️'
        };
      case 'warning':
        return {
          backgroundColor: backgroundColor || '#fff3e0',
          borderColor: borderColor || '#ff9800',
          textColor: textColor || '#e65100',
          defaultIcon: '⚠️'
        };
      case 'success':
        return {
          backgroundColor: backgroundColor || '#e8f5e8',
          borderColor: borderColor || '#4caf50',
          textColor: textColor || '#2e7d32',
          defaultIcon: '✅'
        };
      case 'error':
        return {
          backgroundColor: backgroundColor || '#ffebee',
          borderColor: borderColor || '#f44336',
          textColor: textColor || '#c62828',
          defaultIcon: '❌'
        };
      default:
        return {
          backgroundColor: backgroundColor || '#f5f5f5',
          borderColor: borderColor || '#ccc',
          textColor: textColor || '#333',
          defaultIcon: ''
        };
    }
  };

  const styles = getVariantStyles();
  const finalIcon = icon || styles.defaultIcon;

  // Adjust padding for mobile
  const responsivePadding = isMobile ? Math.max(8, padding * 0.5) : padding;

  return (
    <Box mb={isMobile ? 2 : 3} {...filterDOMProps(props)} style={{ width: '100%', maxWidth: '100%' }}>
      <Paper
        elevation={elevation}
        style={{
          backgroundColor: styles.backgroundColor,
          border: `2px solid ${styles.borderColor}`,
          borderRadius: borderRadius,
          padding: responsivePadding,
          textAlign: centerContent ? 'center' : 'left',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          overflow: 'hidden'
        }}
      >
        {title && (
          <Box display="flex" alignItems="center" justifyContent={centerContent ? 'center' : 'flex-start'} mb={1}>
            {finalIcon && (
              <Typography
                variant="h6"
                style={{
                  marginRight: 8,
                  color: iconColor || styles.textColor,
                  fontSize: '1.2em'
                }}
              >
                {finalIcon}
              </Typography>
            )}
            <Typography
              variant="h6"
              style={{
                fontWeight: 'bold',
                color: styles.textColor,
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                maxWidth: '100%',
                ...titleStyle
              }}
            >
              {processTextContent(title || '', 0, data, baseData, player, playerAssessment)}
            </Typography>
          </Box>
        )}

        {finalItems.length > 0 ? (
          <Box>
            {finalItems.map((item, index) => (
              <Box key={index} mb={0.5}>
                {item.label ? (
                  <Typography
                    variant={isMobile ? "body2" : "body1"}
                    style={{
                      color: styles.textColor,
                      lineHeight: 1.5,
                      overflowWrap: 'anywhere',
                      wordWrap: 'break-word',
                      maxWidth: '100%',
                      fontSize: isMobile ? '0.75rem' : undefined,
                      ...contentStyle
                    }}
                  >
                    <strong>{processTextContent(item.label, 0, data, baseData, player, playerAssessment)}:</strong> {processTextContent(item.value, 0, data, baseData, player, playerAssessment)}
                  </Typography>
                ) : (
                  <Typography
                    variant={isMobile ? "body2" : "body1"}
                    style={{
                      color: styles.textColor,
                      lineHeight: 1.5,
                      overflowWrap: 'anywhere',
                      wordWrap: 'break-word',
                      maxWidth: '100%',
                      fontSize: isMobile ? '0.75rem' : undefined,
                      ...contentStyle
                    }}
                  >
                    {processTextContent(item.value, 0, data, baseData, player, playerAssessment)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant={isMobile ? "body2" : "body1"}
            style={{
              color: styles.textColor,
              lineHeight: 1.5,
              overflowWrap: 'anywhere',
              wordWrap: 'break-word',
              maxWidth: '100%',
              fontSize: isMobile ? '0.75rem' : undefined,
              ...contentStyle
            }}
          >
            {processTextContent(content || 'No content available', 0, data, baseData, player, playerAssessment)}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
